import React, { useEffect } from "react";
import i18n from "./i18n";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { Toaster } from "sonner";
import { ThemeProvider } from "./components/theme-provider";
import ErrorBoundary from "./components/ErrorBoundary";
import Layout from "./components/Layout";
import PortalLayout from "./components/PortalLayout";
import { applyTunisianDefaults } from "./lib/currency";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Employees from "./pages/Employees";
import Pointages from "./pages/Pointages";
import Planning from "./pages/Planning";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import ZktecoMonitoring from "./pages/ZktecoMonitoring";

// Nouveaux modules STS
import PointagesAdvanced from "./pages/PointagesAdvanced";
import ValidationWorkflow from "./pages/ValidationWorkflow";
import TerminalManagement from "./pages/TerminalManagement";
import AdvancedReports from "./pages/AdvancedReports";
import SecurityRoles from "./pages/SecurityRoles";
import SecurityCenter from "./pages/SecurityCenter";

// Modules Société et Organigramme
import CompanyConfiguration from "./pages/CompanyConfiguration";
import Organigramme from "./pages/Organigramme";

// Module de mapping biométrique
import BiometricMapping from "./pages/BiometricMapping";

// Module de gestion des absences
import GestionAbsences from "./pages/GestionAbsences";
import AbsenceReasons from "./pages/AbsenceReasons";
import ApprovalWorkflows from "./pages/ApprovalWorkflows";

// Module de gestion des documents
import DocumentTemplates from "./pages/DocumentTemplates";

// Module de gestion des jours fériés
import JoursFeries from "./pages/JoursFeries";
import JoursFeriesBibliotheque from "./pages/JoursFeriesBibliotheque";
import JoursFeriesCalendriers from "./pages/JoursFeriesCalendriers";
import JoursFeriesCalendrierDetail from "./pages/JoursFeriesCalendrierDetail";
import JoursFeriesAffectations from "./pages/JoursFeriesAffectations";
import MesJoursFeries from "./pages/MesJoursFeries";

// Mon Espace (self-service) + portail employé
import MonEspace from "./pages/MonEspace";
import PortalDashboard from "./pages/PortalDashboard";

// Module de gestion de la paie
import PayrollHub from "./pages/PayrollHub";

// Module Recrutement ATS
import RecruitmentHub from "./pages/RecruitmentHub";

// Module Performance
import PerformanceHub from "./pages/PerformanceHub";

// SSO OAuth2 callback + profile completion
import OAuthCallback from "./pages/OAuthCallback";
import CompleteProfile from "./pages/CompleteProfile";

// E-Signature
import ESignatureHub from "./pages/ESignatureHub";

// Compliance
import ComplianceHub from "./pages/ComplianceHub";

// Engagement
import EngagementHub from "./pages/EngagementHub";

// Onboarding
import OnboardingHub from "./pages/OnboardingHub";

// LMS / Formation
import LMSHub from "./pages/LMSHub";

// Analytics prédictif
import AnalyticsHub from "./pages/AnalyticsHub";

// Succession Planning
import SuccessionHub from "./pages/SuccessionHub";

// GDPR / Protection des données
import GDPRHub from "./pages/GDPRHub";

// Benefits Management
import BenefitsHub from "./pages/BenefitsHub";

// Expense Management
import ExpensesHub from "./pages/ExpensesHub";

// SSO Enterprise
import SSOHub from "./pages/SSOHub";

// Custom Report Builder
import ReportBuilderHub from "./pages/ReportBuilderHub";

// 1-on-1 Meeting Notes
import OneOnOneHub from "./pages/OneOnOneHub";

// Integrations Hub
import IntegrationsHub from "./pages/IntegrationsHub";

// Compensation Management
import CompensationHub from "./pages/CompensationHub";

// Offboarding
import OffboardingHub from "./pages/OffboardingHub";

// Workforce Planning
import WorkforcePlanningHub from "./pages/WorkforcePlanningHub";

// Recognition & Kudos
import RecognitionHub from "./pages/RecognitionHub";

// Skills & Competencies
import SkillsHub from "./pages/SkillsHub";

// Career Pathing & IDP
import CareerHub from "./pages/CareerHub";

// Wellbeing
import WellbeingHub from "./pages/WellbeingHub";

// Position Management
import PositionsHub from "./pages/PositionsHub";
import LeavePolicyHub from "./pages/LeavePolicyHub";
import ExecutiveDashboardHub from "./pages/ExecutiveDashboardHub";
import WorkflowAutomationHub from "./pages/WorkflowAutomationHub";
import InterviewSchedulingHub from "./pages/InterviewSchedulingHub";

// Monitoring & Observabilité
import MonitoringHub from "./pages/MonitoringHub";

// Agents IA
import HRAnalyst from "./pages/HRAnalyst";
import AIContractGenerator from "./pages/AIContractGenerator";
import AuditTrail from "./pages/AuditTrail";
import PayrollClosingAgent from "./pages/PayrollClosingAgent";

// Landing Page
import LandingPageEnriched from "./pages/LandingPageEnriched";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";

// Careers Portal - Public
import CareersLayout from "./pages/careers/CareersLayout";
import CareersJobList from "./pages/careers/CareersJobList";
import CareersJobDetail from "./pages/careers/CareersJobDetail";
import CareersTracking from "./pages/careers/CareersTracking";
import SignDocument from "./pages/SignDocument";

// Registration Wizard
import Registration from "./pages/Registration";

// Admin Dashboard (SaaS management)
import AdminDashboard from "./pages/AdminDashboard";

// Stripe payment return page
import PaymentReturn from "./pages/PaymentReturn";

// 404
import NotFound from "./pages/NotFound";
import AccessDenied from "./pages/AccessDenied";
import { canAccessRoute } from "./lib/permissions";

// Context pour le compteur de départements
import { DepartementCountProvider } from "./context/DepartementCountContext";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const LoadingScreen = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="space-y-2 text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
      <p className="text-sm text-muted-foreground">Chargement...</p>
    </div>
  </div>
);

// Composant de protection des routes admin (bloque les employés)
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) return <LoadingScreen />;

  // Admin dashboard manages its own auth — never intercept /admin* routes
  const currentPath = window.location.pathname;
  if (currentPath.startsWith('/admin')) {
    return <ErrorBoundary name="ProtectedRoute">{children}</ErrorBoundary>;
  }

  // Main domain → landing page for unauthenticated users
  // Tenant subdomains (*.mawered.com or *.mawered.local) → login page
  const hostname = window.location.hostname;
  const isTenantSubdomain =
    hostname !== "mawered.com" &&
    hostname !== "www.mawered.com" &&
    hostname !== "mawered.local" &&
    (hostname.endsWith(".mawered.com") || hostname.endsWith(".mawered.local"));

  if (!isAuthenticated) {
    return <Navigate to={isTenantSubdomain ? "/login" : "/"} replace />;
  }

  // EMPLOYE role must stay in the portal — redirect away from admin routes
  if (user?.roles?.includes("EMPLOYE") && !user?.roles?.some((r) => ["ADMIN", "RH", "SUPERVISOR", "COMPTABLE"].includes(r))) {
    return <Navigate to="/portal/dashboard" replace />;
  }

  // Authenticated but the user's permissions don't cover this specific route
  // (e.g. direct URL entry bypassing the sidebar's own filtering)
  if (!canAccessRoute(user, currentPath)) {
    return <AccessDenied />;
  }

  return <ErrorBoundary name="ProtectedRoute">{children}</ErrorBoundary>;
};

// Composant de protection des routes portail (bloque les non-authentifiés)
const PortalRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <LoadingScreen />;

  const hostname = window.location.hostname;
  const isTenantSubdomain =
    hostname !== "mawered.com" &&
    hostname !== "www.mawered.com" &&
    hostname !== "mawered.local" &&
    (hostname.endsWith(".mawered.com") || hostname.endsWith(".mawered.local"));

  return isAuthenticated ? (
    <ErrorBoundary name="PortalRoute">{children}</ErrorBoundary>
  ) : (
    <Navigate to={isTenantSubdomain ? "/login" : "/"} replace />
  );
};

// Route racine : rend le landing public sur le domaine principal,
// sinon redirige selon l'authentification et le rôle
const HomeRoute = () => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) return <LoadingScreen />;

  // Tenant subdomain detection (e.g. acme.mawered.com → careers portal)
  if (!isAuthenticated) {
    const parts = window.location.hostname.split(".");
    const RESERVED = new Set(["www", "careers", "admin", "api", "traefik", "mawered", "localhost"]);
    if (parts.length > 2 && !RESERVED.has(parts[0])) {
      return <Navigate to="/careers" replace />;
    }
    if (window.location.hostname.startsWith("careers.")) {
      return <Navigate to="/careers" replace />;
    }
    // Domaine principal non authentifié : le landing est rendu directement
    // sur / (canonical https://mawered.com/), plus de redirection /landing
    return (
      <PageWrapper name="LandingPageEnriched">
        <LandingPageEnriched />
      </PageWrapper>
    );
  }

  // EMPLOYE (without elevated role) → portal
  if (user?.roles?.includes("EMPLOYE") && !user?.roles?.some((r) => ["ADMIN", "RH", "SUPERVISOR", "COMPTABLE"].includes(r))) {
    return <Navigate to="/portal/dashboard" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

// Wrapper pour les pages avec ErrorBoundary
const PageWrapper = ({ children, name }) => (
  <ErrorBoundary name={name} showRetry={true} showReload={true} showGoHome={true}>
    {children}
  </ErrorBoundary>
);

function App() {
  // Initialiser les paramètres tunisiens au démarrage de l'application
  useEffect(() => {
    applyTunisianDefaults();
  }, []);

  // Mettre à jour la direction RTL/LTR quand la langue change en cours de session
  // (l'initialisation au chargement est gérée dans index.js pour éviter toute course)
  useEffect(() => {
    const applyDir = (lang) => {
      const base = lang?.split('-')[0];
      document.documentElement.dir = base === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = base || 'fr';
    };
    i18n.on('languageChanged', applyDir);
    return () => i18n.off('languageChanged', applyDir);
  }, []);
  // App component - Mawered Mawered
  return (
    <QueryClientProvider client={queryClient}>
    <ErrorBoundary name="App" showRetry={true} showReload={true}>
      <ThemeProvider defaultTheme="dark" storageKey="hr-suite-ui-theme">
        <AuthProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* /landing a fusionné avec la racine. Le 301 permanent est fait
                    côté serveur (nginx) ; ceci est le filet côté client. */}
                <Route path="/landing" element={<Navigate to="/" replace />} />

                {/* Public document signing (built-in SES, no auth) */}
                <Route
                  path="/sign/:token"
                  element={
                    <PageWrapper name="SignDocument">
                      <SignDocument />
                    </PageWrapper>
                  }
                />

                {/* Privacy Policy - Public */}
                <Route
                  path="/privacy"
                  element={
                    <PageWrapper name="PrivacyPolicy">
                      <PrivacyPolicy />
                    </PageWrapper>
                  }
                />

                {/* Terms of Service - Public */}
                <Route
                  path="/terms"
                  element={
                    <PageWrapper name="TermsOfService">
                      <TermsOfService />
                    </PageWrapper>
                  }
                />

                {/* Registration Wizard - Public */}
                <Route
                  path="/register"
                  element={
                    <PageWrapper name="Registration">
                      <Registration />
                    </PageWrapper>
                  }
                />

                {/* Stripe Payment Return - Public */}
                <Route
                  path="/payment/return"
                  element={
                    <PageWrapper name="PaymentReturn">
                      <PaymentReturn />
                    </PageWrapper>
                  }
                />

                {/* Admin Dashboard - SaaS Organization Management */}
                <Route
                  path="/admin-dashboard"
                  element={
                    <PageWrapper name="AdminDashboard">
                      <AdminDashboard />
                    </PageWrapper>
                  }
                />

                {/* Route de connexion - SANS les contextes qui font des appels API */}
                <Route
                  path="/login"
                  element={
                    <PageWrapper name="Login">
                      <Login />
                    </PageWrapper>
                  }
                />

                <Route
                  path="/forgot-password"
                  element={
                    <PageWrapper name="ForgotPassword">
                      <ForgotPassword />
                    </PageWrapper>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <PageWrapper name="ResetPassword">
                      <ResetPassword />
                    </PageWrapper>
                  }
                />

                {/* SSO OAuth2 callback — public, no auth required */}
                <Route
                  path="/auth/callback"
                  element={
                    <PageWrapper name="OAuthCallback">
                      <OAuthCallback />
                    </PageWrapper>
                  }
                />

                {/* SAML ACS redirect — same handler as OAuth2 callback */}
                <Route
                  path="/sso/callback"
                  element={
                    <PageWrapper name="OAuthCallback">
                      <OAuthCallback />
                    </PageWrapper>
                  }
                />

                {/* Social auth profile completion — public, no auth required */}
                <Route
                  path="/complete-profile"
                  element={
                    <PageWrapper name="CompleteProfile">
                      <CompleteProfile />
                    </PageWrapper>
                  }
                />

                {/* Racine : landing public (domaine principal) ou redirection auth/rôle */}
                <Route index element={<HomeRoute />} />

                {/* Portal employé — layout minimal sans sidebar */}
                <Route
                  path="/portal"
                  element={
                    <PortalRoute>
                      <PortalLayout />
                    </PortalRoute>
                  }
                >
                  <Route index element={<Navigate to="dashboard" replace />} />
                  <Route
                    path="dashboard"
                    element={
                      <PageWrapper name="PortalDashboard">
                        <PortalDashboard />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="mon-espace"
                    element={
                      <PageWrapper name="MonEspace">
                        <MonEspace />
                      </PageWrapper>
                    }
                  />
                </Route>

                {/* Routes protégées - AVEC DepartementCountProvider */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <DepartementCountProvider>
                        <Layout />
                      </DepartementCountProvider>
                    </ProtectedRoute>
                  }
                >

                  {/* Pages principales */}
                  <Route
                    path="dashboard"
                    element={
                      <PageWrapper name="Dashboard">
                        <Dashboard />
                      </PageWrapper>
                    }
                  />

                  {/* Configuration Société */}
                  <Route
                    path="company-configuration"
                    element={
                      <PageWrapper name="CompanyConfiguration">
                        <CompanyConfiguration />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="organigramme"
                    element={
                      <PageWrapper name="Organigramme">
                        <Organigramme />
                      </PageWrapper>
                    }
                  />

                  {/* Gestion RH */}
                  <Route
                    path="employees"
                    element={
                      <PageWrapper name="Employees">
                        <Employees />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="planning"
                    element={
                      <PageWrapper name="Planning">
                        <Planning />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="gestion-absences"
                    element={
                      <PageWrapper name="GestionAbsences">
                        <GestionAbsences />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="document-templates"
                    element={
                      <PageWrapper name="DocumentTemplates">
                        <DocumentTemplates />
                      </PageWrapper>
                    }
                  />
                  {/* Routes anciennes conservées pour rétrocompatibilité */}
                  <Route
                    path="absence-reasons"
                    element={
                      <PageWrapper name="AbsenceReasons">
                        <AbsenceReasons />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="approval-workflows"
                    element={
                      <PageWrapper name="ApprovalWorkflows">
                        <ApprovalWorkflows />
                      </PageWrapper>
                    }
                  />

                  {/* Jours Fériés */}
                  <Route
                    path="jours-feries"
                    element={
                      <PageWrapper name="JoursFeries">
                        <JoursFeries />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="jours-feries/calendriers/:id"
                    element={
                      <PageWrapper name="JoursFeriesCalendrierDetail">
                        <JoursFeriesCalendrierDetail />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="recrutement"
                    element={
                      <PageWrapper name="RecruitmentHub">
                        <RecruitmentHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="performance"
                    element={
                      <PageWrapper name="PerformanceHub">
                        <PerformanceHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="esignature"
                    element={
                      <PageWrapper name="ESignatureHub">
                        <ESignatureHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="compliance"
                    element={
                      <PageWrapper name="ComplianceHub">
                        <ComplianceHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="engagement"
                    element={
                      <PageWrapper name="EngagementHub">
                        <EngagementHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="onboarding"
                    element={
                      <PageWrapper name="OnboardingHub">
                        <OnboardingHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="lms"
                    element={
                      <PageWrapper name="LMSHub">
                        <LMSHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="analytics"
                    element={
                      <PageWrapper name="AnalyticsHub">
                        <AnalyticsHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="succession"
                    element={
                      <PageWrapper name="SuccessionHub">
                        <SuccessionHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="gdpr"
                    element={
                      <PageWrapper name="GDPRHub">
                        <GDPRHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="benefits"
                    element={
                      <PageWrapper name="BenefitsHub">
                        <BenefitsHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="expenses"
                    element={
                      <PageWrapper name="ExpensesHub">
                        <ExpensesHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="sso"
                    element={
                      <PageWrapper name="SSOHub">
                        <SSOHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="report-builder"
                    element={
                      <PageWrapper name="ReportBuilderHub">
                        <ReportBuilderHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="one-on-one"
                    element={
                      <PageWrapper name="OneOnOneHub">
                        <OneOnOneHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="integrations"
                    element={
                      <PageWrapper name="IntegrationsHub">
                        <IntegrationsHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="compensation"
                    element={
                      <PageWrapper name="CompensationHub">
                        <CompensationHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="offboarding"
                    element={
                      <PageWrapper name="OffboardingHub">
                        <OffboardingHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="workforce-planning"
                    element={
                      <PageWrapper name="WorkforcePlanningHub">
                        <WorkforcePlanningHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="recognition"
                    element={
                      <PageWrapper name="RecognitionHub">
                        <RecognitionHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="skills"
                    element={
                      <PageWrapper name="SkillsHub">
                        <SkillsHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="career"
                    element={
                      <PageWrapper name="CareerHub">
                        <CareerHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="wellbeing"
                    element={
                      <PageWrapper name="WellbeingHub">
                        <WellbeingHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="positions"
                    element={
                      <PageWrapper name="PositionsHub">
                        <PositionsHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="leave-policies"
                    element={
                      <PageWrapper name="LeavePolicyHub">
                        <LeavePolicyHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="executive-dashboard"
                    element={
                      <PageWrapper name="ExecutiveDashboardHub">
                        <ExecutiveDashboardHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="workflow-automation"
                    element={
                      <PageWrapper name="WorkflowAutomationHub">
                        <WorkflowAutomationHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="interview-scheduling"
                    element={
                      <PageWrapper name="InterviewSchedulingHub">
                        <InterviewSchedulingHub />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="paie"
                    element={
                      <PageWrapper name="PayrollHub">
                        <PayrollHub />
                      </PageWrapper>
                    }
                  />
                  <Route path="payroll" element={<Navigate to="/paie" replace />} />

                  {/* Pointages */}
                  <Route
                    path="pointages"
                    element={
                      <PageWrapper name="Pointages">
                        <Pointages />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="pointages-advanced"
                    element={
                      <PageWrapper name="PointagesAdvanced">
                        <PointagesAdvanced />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="validation-workflow"
                    element={
                      <PageWrapper name="ValidationWorkflow">
                        <ValidationWorkflow />
                      </PageWrapper>
                    }
                  />

                  {/* Infrastructure */}
                  <Route
                    path="terminals"
                    element={
                      <PageWrapper name="TerminalManagement">
                        <TerminalManagement />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="biometric-mapping"
                    element={
                      <PageWrapper name="BiometricMapping">
                        <BiometricMapping />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="zkteco-monitoring"
                    element={
                      <PageWrapper name="ZktecoMonitoring">
                        <ZktecoMonitoring />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="monitoring"
                    element={
                      <PageWrapper name="MonitoringHub">
                        <MonitoringHub />
                      </PageWrapper>
                    }
                  />

                  {/* Rapports & Analytics */}
                  <Route
                    path="reports"
                    element={
                      <PageWrapper name="Reports">
                        <Reports />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="advanced-reports"
                    element={
                      <PageWrapper name="AdvancedReports">
                        <AdvancedReports />
                      </PageWrapper>
                    }
                  />

                  {/* Administration */}
                  <Route
                    path="settings"
                    element={
                      <PageWrapper name="Settings">
                        <Settings />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="hr-analyst"
                    element={
                      <PageWrapper name="HRAnalyst">
                        <HRAnalyst />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="ai-contract-generator"
                    element={
                      <PageWrapper name="AIContractGenerator">
                        <AIContractGenerator />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="audit-trail"
                    element={
                      <PageWrapper name="AuditTrail">
                        <AuditTrail />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="payroll-closing-agent"
                    element={
                      <PageWrapper name="PayrollClosingAgent">
                        <PayrollClosingAgent />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="security-roles"
                    element={
                      <PageWrapper name="SecurityRoles">
                        <SecurityRoles />
                      </PageWrapper>
                    }
                  />
                  <Route
                    path="security-center"
                    element={
                      <PageWrapper name="SecurityCenter">
                        <SecurityCenter />
                      </PageWrapper>
                    }
                  />
                </Route>

                {/* Careers Portal - Public (no auth) */}
                <Route path="/careers" element={<CareersLayout />}>
                  <Route index element={<PageWrapper name="CareersJobList"><CareersJobList /></PageWrapper>} />
                  <Route path=":id" element={<PageWrapper name="CareersJobDetail"><CareersJobDetail /></PageWrapper>} />
                  <Route path=":id/apply" element={<PageWrapper name="CareersJobDetail"><CareersJobDetail /></PageWrapper>} />
                  <Route path="track" element={<PageWrapper name="CareersTracking"><CareersTracking /></PageWrapper>} />
                  <Route path="track/:token" element={<PageWrapper name="CareersTracking"><CareersTracking /></PageWrapper>} />
                </Route>

                {/* 404 - page non trouvée */}
                <Route path="*" element={<NotFound />} />
              </Routes>

              {/* Système de notifications global avec Sonner */}
              <Toaster
                position="top-right"
                expand={true}
                richColors={true}
                toastOptions={{
                  style: {
                    background: "hsl(var(--background))",
                    color: "hsl(var(--foreground))",
                    border: "1px solid hsl(var(--border))",
                  },
                }}
              />
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
    {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
